/** The `flows` area: the Flows page and a flow's page: stages, fields, health
 *  rules, findings. Owned by the stage 2 slot flows (docs/I18N.md §11). The
 *  keys read in page order: the index and New flow, then a flow's page: its
 *  head and access, the save bar and archive, the findings, the stage editor,
 *  the save refusal, the fields and the health rules. A stage key or a field
 *  key is shown as it is, never translated; only its label is. */
export const flows = {
  // --- The Flows index ---
  'flows.index.title': 'Flows',
  /** The sub-line with no flow yet. */
  'flows.index.sub': 'how items move',
  'flows.index.count': { one: '{count} flow', other: '{count} flows' },
  'flows.index.empty':
    'No flows yet. A flow is how items move - ordered stages, decisions at the boundaries, its own fields - reaching whoever its access says, and boards can take their columns from it. Create one and link it from a board’s Configure.',
  'flows.index.archived': 'Archived',
  'flows.sort.name': 'Name (A–Z)',
  'flows.sort.phases': 'Most stages',
  'flows.sort.menu': 'Sort by',
  'flows.sort.label': 'Sort flows',
  /** A flow card's counts. */
  'flows.card.phases': { one: '{count} stage', other: '{count} stages' },
  'flows.card.fields': { one: '{count} field', other: '{count} fields' },
  // --- New flow ---
  'flows.new.open': 'New flow',
  'flows.new.title': 'New flow',
  'flows.new.cancel': 'Cancel',
  'flows.new.create': 'Create',
  'flows.new.name': 'Name',
  'flows.new.nameHint': 'Free vocabulary; rename anytime (the stable key is minted once).',
  'flows.new.namePlaceholder': 'e.g. Delivery, Hiring, NPD',
  'flows.new.nameLabel': 'Flow name',
  'flows.new.owner': 'Owner',
  'flows.new.ownerHint':
    'The context that owns and governs this flow; its own boards can take their columns from it.',
  'flows.new.ownerLabel': 'Flow owner',
  // --- What a new flow is born with (written in the creator's language, then
  // stored as the flow's own content: docs/I18N.md §7, "A default is written
  // in its creator's language") ---
  'flows.starter.stage.toDo': 'To do',
  'flows.starter.stage.inProgress': 'In progress',
  'flows.starter.stage.done': 'Done',
  /** A personal flow's name. */
  'flows.starter.name.personal': 'My flow',
  /** A group's flow, named after the group. */
  'flows.starter.name.group': '{group} flow',
  /** A group's flow whose group the creator cannot read. */
  'flows.starter.name.fallback': 'New flow',
  // --- A flow's page: the head ---
  /** The meta line beside the flow's name, its parts joined by " · ": what it
   *  is, its owners' names joined (dropped on a phone), and whether it is
   *  archived. */
  'flows.detail.kind': 'flow',
  'flows.detail.owners': 'owned by {owners}',
  'flows.detail.archived': 'archived',
  // --- Access ---
  'flows.access.bubbles': 'Access to {flow}',
  'flows.access.title': 'Access',
  'flows.access.loading': 'Loading…',
  'flows.access.unreachable': 'This flow is not reachable.',
  'flows.access.footnote':
    "The audience sees this flow and the items shared into it. Access changes are recorded in the flow's activity.",
  // --- The save bar, the actions menu and archive ---
  'flows.config.name': 'Name',
  'flows.config.nameHint': 'Renaming breaks nothing.',
  'flows.config.nameLabel': 'Flow name',
  'flows.config.fixFirst': 'Fix the highlighted problems before saving.',
  'flows.config.discardAsk': 'Discard all unsaved changes?',
  'flows.config.discard': 'Discard',
  'flows.config.keepEditing': 'Keep editing',
  'flows.config.discardChanges': 'Discard changes',
  'flows.config.save': 'Save changes',
  'flows.config.actionsFor': 'Actions for {flow}',
  'flows.config.actions': 'Flow actions',
  'flows.config.unarchive': 'Un-archive',
  'flows.config.archive': 'Archive…',
  'flows.archive.title': 'Archive this flow?',
  'flows.archive.keep': 'Keep it',
  'flows.archive.confirm': 'Archive flow',
  'flows.archive.body':
    'History stays and linked boards fall back to status columns; members stop moving through it. You can un-archive it later from this menu.',
  'flows.archive.note':
    'Archived: history stays, nothing new moves through it; linked boards fall back to status columns.',
  // --- Findings ---
  'flows.findings.label': 'Flow findings',
  /** A finding's kind, as a canon word. */
  'flows.findings.word.noUndo': 'Rule refused: undo',
  'flows.findings.word.noRevisit': 'Rule refused: churn',
  'flows.findings.word.hopBudget': 'Chain stopped',
  'flows.findings.word.potentialCycle': 'Possible cycle',
  'flows.findings.word.dispatch': 'Act refused',
  /** What each kind means, behind the (?). */
  'flows.findings.hint.noUndo':
    'A rule tried to undo the very act that triggered it, so its act was refused. A rule that undoes its own trigger is usually a requirement written as an action - move the condition into "Card may move here when".',
  'flows.findings.hint.noRevisit':
    'A rule tried to set a value this chain of automations had already produced once, so its act was refused - that is churn, not progress.',
  'flows.findings.hint.hopBudget':
    'One act set off more machine moves than the budget allows (8), so the chain was stopped here.',
  'flows.findings.hint.potentialCycle':
    'Saved with a warning: under some conditions these rules can chain back into themselves. Nothing was refused yet.',
  'flows.findings.hint.dispatch':
    "A rule's act was refused when it ran: it was no longer legal for the item, or the flow lacked the right.",
  'flows.findings.hint.unknown': "The flow's rules reported this while running.",
  /** The flow version a finding was recorded under. */
  'flows.findings.version': 'v{version}',
  'flows.findings.about': 'About this finding',
  /** The rule key that reported, shown in mono. */
  'flows.findings.rule': 'Rule: {key}',
  // --- The stage editor: the stage list ---
  'flows.stages.title': 'Stages',
  'flows.stage.untitled': 'Untitled stage',
  'flows.stage.about': 'About this stage',
  'flows.stage.moveUp': 'Move stage {stage} up',
  'flows.stage.moveDown': 'Move stage {stage} down',
  'flows.stage.expand': 'Expand stage {stage}',
  'flows.stage.edit': 'Edit stage {stage}',
  'flows.stage.collapse': 'Collapse stage',
  'flows.stage.add': '+ Add stage',
  /** The collapsed stage's spec line: its means pairs. */
  'flows.stage.means': 'means',
  'flows.stage.onLeaving.keep': '(on leaving: keep)',
  'flows.stage.onLeaving.clear': '(on leaving: clear)',
  /** A stage's (?) sentences, composed from what the stage does. */
  'flows.stage.help.applies':
    'Moving a card here applies the listed values in one step; a value that cannot be set refuses the whole move. When an act makes ALL the listed values true, the card moves here by itself, on the record.',
  'flows.stage.help.requirements':
    'A move that does not meet the listed requirements is refused, with the reason named.',
  'flows.stage.help.whoRequester': 'Only requesters may move cards here.',
  'flows.stage.help.whoContributor': 'Only contributors may move cards here.',
  'flows.stage.help.whoOwner': 'Only flow owners may move cards here.',
  'flows.stage.help.manual':
    'A manual column: moving a card here changes nothing on the item, and nothing moves cards here by itself.',
  /** Inline problems with a stage draft. */
  'flows.stage.problem.label': 'A stage needs a label.',
  'flows.stage.problem.applies': 'A meaning names each attribute once.',
  // --- The stage editor: the open panel ---
  'flows.stage.label': 'Label',
  'flows.stage.labelAbout': 'About the label',
  'flows.stage.labelHelp': 'Shown on cards, board columns, and the item page. Change it anytime.',
  'flows.stage.labelField': 'Stage label',
  'flows.stage.description': 'Description',
  'flows.stage.descriptionAbout': 'About the description',
  'flows.stage.descriptionHelp':
    'Optional. A note in your own words on what this stage is for - shown when someone hovers the board column. It sits alongside the actions the stage applies, never replacing them.',
  'flows.stage.descriptionField': 'Stage description',
  'flows.stage.descriptionPlaceholder': 'What this stage is for (optional)',
  /** Who may move cards into the stage. */
  'flows.stage.who': 'Who may move cards here',
  'flows.stage.whoAbout': 'About who may move cards here',
  'flows.stage.whoHelp':
    'Without a choice, members may move cards and an out-of-role move is flagged on the record, never blocked. Choosing turns the flag into a refusal.',
  'flows.stage.who.members': 'Members',
  'flows.stage.who.requesters': 'Requesters',
  'flows.stage.who.contributors': 'Contributors',
  'flows.stage.who.owners': 'Flow owners',
  /** The requirements table. */
  'flows.stage.requirements': 'Card may move here when',
  'flows.stage.requirementsAbout': 'About requirements',
  'flows.stage.requirementsHelp':
    'A move that does not meet a requirement is refused, and the requirement is named at the refusal.',
  'flows.stage.attribute': 'Attribute',
  'flows.stage.mustBe': 'Must be',
  'flows.stage.value': 'Value',
  'flows.stage.removeRequirement': 'Remove requirement',
  'flows.stage.addRequirement': '+ add requirement',
  /** The applies list: the stage's means pairs. */
  'flows.stage.applies': 'Moving a card here applies',
  'flows.stage.appliesAbout': 'About applied values',
  'flows.stage.appliesHelp':
    'What being here MEANS: the move applies these values in one step (if any cannot be set, the whole move is refused), and a card whose values just became exactly these moves here by itself, on the record. "On leaving" exists where the value is clearable: health states and fields offer keep or clear. Acts like marking done are not undone by leaving a column.',
  'flows.stage.onLeavingInline': 'on leaving',
  'flows.stage.onLeaving': 'On leaving',
  'flows.stage.removeApplied': 'Remove applied value',
  'flows.stage.addPair': '+ add pair',
  /** The attribute picker's dimensions. */
  'flows.stage.attr.delivered': 'Completion',
  'flows.stage.attr.closed': 'Conclusion',
  'flows.stage.attr.active': 'Active',
  'flows.stage.attr.verdict': 'Verdict',
  'flows.stage.attr.cancelled': 'Cancelled',
  'flows.stage.attr.health': 'Health',
  /** The value pickers' words. */
  'flows.stage.val.yes': 'yes',
  'flows.stage.val.no': 'no',
  'flows.stage.val.worn': 'worn',
  'flows.stage.val.clear': 'clear',
  'flows.stage.val.noVerdict': 'none yet',
  'flows.stage.val.accepted': 'Accepted',
  'flows.stage.val.denied': 'Denied',
  'flows.stage.val.keep': 'keep',
  'flows.stage.val.clearOnLeaving': 'clear',
  'flows.stage.val.any': 'any value',
  'flows.stage.val.placeholder': 'value',
  'flows.stage.pickState': 'Pick a state',
  /** The automations' questions and their answers. */
  'flows.policy.title': 'When an automation asks',
  'flows.policy.about': 'About automation answers',
  'flows.policy.help':
    'An automation never waits for a person. When its act raises a question - sub-items still open under a close, or an act outside its role - the answer written here is what it does. "Ask the requester" files the question with the item’s requester once, however often it fires; "Skip with a finding" does nothing and tells you on this flow.',
  'flows.policy.automation': 'Automation',
  'flows.policy.question': 'Question',
  'flows.policy.answer': 'Answer',
  'flows.policy.onEntering': 'On entering: {command}',
  'flows.policy.onLeaving': 'On leaving: {command}',
  'flows.policy.answerFor': 'Answer to "{question}" for {command}',
  'flows.policy.class.openChildren': 'Sub-items still open',
  'flows.policy.class.outOfRole': 'Not its role',
  'flows.policy.class.cascadeSize': 'Too large',
  'flows.policy.leaveOpen': 'Leave open',
  'flows.policy.closeSafe': 'Close the safe set',
  'flows.policy.closeAll': 'Close them all',
  'flows.policy.askRequester': 'Ask the requester',
  'flows.policy.skip': 'Skip with a finding',
  'flows.policy.goAhead': 'Yes, act anyway',
  /** Removing a stage. */
  'flows.stage.removeAsk':
    'Remove this stage? Cards keep their history; the column disappears from boards.',
  'flows.stage.remove': 'Remove stage',
  'flows.stage.keep': 'Keep it',
  // --- The save refusal ---
  'flows.refusal.overlap': "Can't save: two stages can mean the same state",
  'flows.refusal.overlapHelp':
    'Two stages whose meanings can hold at once leave the machine two destinations. Narrow one stage’s pairs.',
  'flows.refusal.jointly': 'both hold under · {jointly}',
  'flows.refusal.cycle': "Can't save: these automations loop back into themselves",
  'flows.refusal.cycleHelp':
    'These stages’ automations would chain back into themselves regardless of state, so the save is refused. Re-wire one side: a "must not" belongs in "Card may move here when", never in an action that throws the card back.',
  'flows.refusal.plain': "Can't save",
  'flows.refusal.about': 'About this refusal',
  /** One step of a refused cycle: a stage and the edge that fired. */
  'flows.refusal.step': '{stage} · {edge}',
  'flows.refusal.edge.enter': 'entering',
  'flows.refusal.edge.leave': 'leaving',
  'flows.refusal.edge.pull': 'the pull',
  // --- Fields ---
  'flows.fields.title': 'Fields this flow defines',
  /** Where a field is in use, for the fields editor's remove confirm. */
  'flows.fields.requiredToEnter': 'required to enter {stage}',
  'flows.fields.appliedBy': 'applied by {stage}',
  // --- Health rules ---
  'flows.rules.title': 'Health rules',
  /** The rules' explanation; `callIt` and `suggest` are the two firing modes, in bold. */
  'flows.rules.help':
    'What this flow calls a fact once it passes a number you choose. {callIt} puts the word on every member it holds for - the item wears it, everyone who can see the item sees it, and it lifts when the fact does. {suggest} asks this flow’s people instead: nothing changes colour until someone accepts, and only people who can see this flow are asked. An item in no flow keeps the fact and no word.',
  'flows.rules.callIt': 'Call it',
  'flows.rules.suggest': 'Suggest calling it',
  /** The evidence under the rules: counts only. */
  'flows.evidence.holding': 'Holding now: {list}',
  /** One firing rule: how many members, its word, its grade. */
  'flows.evidence.firing': '{items} × {word} ({grade})',
  'flows.evidence.grade.notice': 'notice',
  'flows.evidence.grade.warning': 'warning',
  'flows.evidence.grade.critical': 'critical',
  'flows.evidence.criticals':
    'Critical set by these rules in the last 30 days: {count}. A critical interrupts everyone with a stake.',
  'flows.evidence.ungraded': {
    one: 'Unnamed here, across {count} member: {list}. Worth a rule?',
    other: 'Unnamed here, across {count} members: {list}. Worth a rule?',
  },
  /** One ungraded fact: its word, on how many members. */
  'flows.evidence.ungradedEntry': '{fact} on {items}',
  /** What a grade costs, where grades are authored (domain/channels.ts
   *  `CHANNEL_MEANING`, HS-D4). */
  'flows.channel.info': 'dashboard only - a raw fact, no colour',
  'flows.channel.notice': 'dashboard only - visible where you already look, never pages a party',
  'flows.channel.warning':
    'colours the dot and enters the counts on the routed surfaces; never pages a party',
  'flows.channel.critical': 'page-grade: pushes to parties and subscribers - keep it rare',
  /** What a boundary into a stage still waits on (domain/flows.ts, the
   *  advance and the item's flow strip). */
  'flows.unmet.condition': '"{stage}" gate: condition {condition} is not accepted.',
  'flows.unmet.requiredFields': '"{stage}" requires fields to be filled: {fields}.',
  'flows.unmet.gate': 'The gate into "{stage}" does not pass.',
  /** Unmet lines side by side. */
  'flows.unmet.joined': '{first} · {second}',
} as const;
